import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import API from '../../utils/apiconfig';
import { TruncateMemberCell } from '../../components/TruncateText';
import DataTable from '../../components/ui/DataTable';
import ListToolbar, { ToolbarField } from '../../components/ui/ListToolbar';
import StatusBadge from '../../components/ui/StatusBadge';
import { ds } from '../../design-system';
import { formatAppDateTime } from '../../utils/dateFormat';

const adminHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_access_token')}`,
});

const COLUMNS = [
  { key: 'when', label: 'When' },
  { key: 'event', label: 'Event' },
  { key: 'earner', label: 'Earner (got points)' },
  { key: 'from', label: 'From member (paid EMI)' },
  { key: 'level', label: 'Level' },
  { key: 'month', label: 'EMI month' },
  { key: 'amount', label: 'Amount (₹)' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'refs', label: 'Refs' },
];

function formatWhen(iso) {
  return formatAppDateTime(iso);
}

function formatPts(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return value ?? '—';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function EventBadge({ event }) {
  const credited = String(event).toUpperCase() === 'CREDITED';
  return (
    <StatusBadge tone={credited ? 'success' : 'danger'}>
      {credited ? 'Credited' : 'Reversed'}
    </StatusBadge>
  );
}

function LedgerBadge({ status }) {
  const s = String(status || '').toUpperCase();
  const tone = s === 'ACTIVE' ? 'info' : s === 'REVERSED' ? 'danger' : 'neutral';
  return <StatusBadge tone={tone} uppercase={false}>{s || '—'}</StatusBadge>;
}

export default function MlmCommissionLogTab() {
  const [view, setView] = useState('events');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({ total_pages: 1, count: 0 });
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [monthNo, setMonthNo] = useState('');
  const [level, setLevel] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async (nextPage = page, overrides = null) => {
    setLoading(true);
    setError('');
    const f = overrides || {
      search,
      eventFilter,
      monthNo,
      level,
      dateFrom,
      dateTo,
      view,
    };
    try {
      const params = {
        page: nextPage,
        page_size: pageSize,
      };
      if (String(f.search || '').trim()) params.search = String(f.search).trim();
      if (f.monthNo) params.month_no = f.monthNo;
      if (f.level) params.level = f.level;
      if (f.dateFrom) params.date_from = f.dateFrom;
      if (f.dateTo) params.date_to = f.dateTo;
      if (f.view === 'events' && f.eventFilter && f.eventFilter !== 'all') {
        params.event = f.eventFilter;
      }

      const url = f.view === 'active'
        ? API.ADMIN_MLM_ACTIVE_LEDGER
        : API.ADMIN_MLM_COMMISSION_LOG;

      const res = await api.get(url, {
        headers: adminHeaders(),
        params,
      });
      const list = res.data?.results || [];
      setRows(list);
      setSummary(res.data?.summary || null);
      setMeta({
        page: res.data?.page || nextPage,
        total_pages: res.data?.total_pages || 1,
        count: res.data?.count || list.length,
      });
      setPage(nextPage);
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to load MLM commission history.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, eventFilter, monthNo, level, dateFrom, dateTo, view]);

  const clearFilters = () => {
    const cleared = {
      search: '',
      eventFilter: 'all',
      monthNo: '',
      level: '',
      dateFrom: '',
      dateTo: '',
      view: 'events',
    };
    setSearch(cleared.search);
    setEventFilter(cleared.eventFilter);
    setMonthNo(cleared.monthNo);
    setLevel(cleared.level);
    setDateFrom(cleared.dateFrom);
    setDateTo(cleared.dateTo);
    setView(cleared.view);
    load(1, cleared);
  };

  // Initial + page-size only. View / event / other filters apply via Apply Filters.
  useEffect(() => {
    load(1);
  }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional

  return (
    <div className="space-y-4">
      {summary ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: 'Credit events', value: summary.credit_events ?? 0, sub: `₹${formatPts(summary.total_credited)} credited`, subClass: 'text-success-700' },
            { label: 'Reversal events', value: summary.reversal_events ?? 0, sub: `₹${formatPts(summary.total_reversed)} reversed`, subClass: 'text-danger-700' },
            { label: 'Net (audit)', value: `₹${formatPts(summary.net_from_audit)}`, sub: 'Credits − reversals', subClass: 'text-ink-muted' },
            { label: 'Active ledger', value: summary.active_ledger_rows ?? 0, sub: `₹${formatPts(summary.active_ledger_amount)} on books`, subClass: 'text-info-600' },
          ].map((card) => (
            <div key={card.label} className={`${ds.card} p-4`}>
              <p className={ds.label}>{card.label}</p>
              <p className="text-xl font-black text-ink mt-1">{card.value}</p>
              <p className={`text-xs mt-1 ${card.subClass}`}>{card.sub}</p>
            </div>
          ))}
        </div>
      ) : null}

      <ListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Earner or from-user / referral code"
        onApply={() => load(1)}
        onClear={clearFilters}
        header={(
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setView('events')} className={view === 'events' ? ds.tabActive : ds.tabIdle}>
              Full event log (credits + reversals)
            </button>
            <button type="button" onClick={() => setView('active')} className={view === 'active' ? ds.tabActive : ds.tabIdle}>
              Active credits only
            </button>
          </div>
        )}
        footer={(
          <p className="text-xs text-ink-muted">
            {view === 'events'
              ? 'Shows every MLM credit and payment-revert reversal. Reversed rows stay in this log even after the ledger entry is cleared.'
              : 'Shows commission rows currently on the books (not yet reversed).'}
          </p>
        )}
      >
        {view === 'events' ? (
          <ToolbarField label="Event" className="w-36">
            <select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)} className={ds.select}>
              <option value="all">All</option>
              <option value="credited">Credited</option>
              <option value="reversed">Reversed</option>
            </select>
          </ToolbarField>
        ) : null}
        <ToolbarField label="EMI month" className="w-28">
          <select value={monthNo} onChange={(e) => setMonthNo(e.target.value)} className={ds.select}>
            <option value="">All</option>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>Month {m}</option>
            ))}
          </select>
        </ToolbarField>
        <ToolbarField label="Level" className="w-28">
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={ds.select}>
            <option value="">All</option>
            {Array.from({ length: 7 }, (_, i) => i + 1).map((lv) => (
              <option key={lv} value={lv}>L{lv}</option>
            ))}
          </select>
        </ToolbarField>
        <ToolbarField label="From" className="w-36">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={ds.input} />
        </ToolbarField>
        <ToolbarField label="To" className="w-36">
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={ds.input} />
        </ToolbarField>
      </ListToolbar>

      {error ? (
        <div className="rounded-ds-xl border border-danger-100 bg-danger-50 px-4 py-3 text-sm text-danger-800">{error}</div>
      ) : null}

      <DataTable
        columns={COLUMNS}
        loading={loading}
        empty={!loading && rows.length === 0}
        emptyTitle="No MLM commission rows found."
        minWidth="72rem"
        mobileScrollHint="Swipe sideways — When stays visible"
        ariaLabel="MLM commission history"
        page={meta.page || page}
        totalPages={meta.total_pages || 1}
        pageSize={pageSize}
        count={meta.count || rows.length}
        onPageChange={(p) => load(p)}
        onPageSizeChange={(next) => { setPageSize(next); setPage(1); }}
        paginationDisabled={loading}
        rowsPerPageId="admin-mlm-commission-log-rows"
      >
        {rows.map((row) => (
          <tr key={row.id} className={ds.row}>
            <td className={`${ds.td} whitespace-nowrap text-ink-muted`}>{formatWhen(row.created_at)}</td>
            <td className={ds.td}><EventBadge event={row.event} /></td>
            <td className={ds.td}>
              <TruncateMemberCell
                username={row.earner_username}
                email={row.earner_referral_code || '—'}
                usernameClassName="font-bold text-ink max-w-[140px]"
                emailClassName="text-[11px] text-ink-subtle max-w-[140px]"
              />
            </td>
            <td className={ds.td}>
              <TruncateMemberCell
                username={row.from_username}
                email={row.from_referral_code || '—'}
                usernameClassName="font-bold text-ink max-w-[140px]"
                emailClassName="text-[11px] text-ink-subtle max-w-[140px]"
              />
            </td>
            <td className={`${ds.td} font-semibold text-ink`}>L{row.level ?? '—'}</td>
            <td className={`${ds.td} text-ink`}>M{row.month_no ?? '—'}</td>
            <td className={`${ds.td} font-black ${String(row.event).toUpperCase() === 'REVERSED' ? 'text-danger-700' : 'text-success-700'}`}>
              {String(row.event).toUpperCase() === 'REVERSED' ? '−' : '+'}
              {formatPts(row.amount)}
            </td>
            <td className={ds.td}><LedgerBadge status={row.ledger_status} /></td>
            <td className={`${ds.td} text-[11px] text-ink-muted whitespace-nowrap`}>
              <div>Txn #{row.mlm_transaction_id ?? '—'}</div>
              <div>Inst #{row.installment_id ?? '—'}</div>
              <div>Proof #{row.payment_proof_id ?? '—'}</div>
            </td>
          </tr>
        ))}
      </DataTable>
    </div>
  );
}
